/* eslint-disable @typescript-eslint/no-unused-vars */
/** @jsxImportSource @emotion/react */
import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { CancelAction, DownloadQueryHint, SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { KolSearchStatus, KolSearchStatusEnum } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import type { KolSearchForm, KolSearchResult } from '@trinity/chat';
import { ActiveChatSessionContext, KolSearchResultsContext, SubmitActionContext } from './context';
import { GenerateRouteButton } from './GenerateRouteButton';
import { useIsSmartRouting } from './hooks/useIsSmartRouting';
import { KolSearchInputForm } from './KolSearch.Form';
import { KolResultsContainer } from './KolSearch.Table';
import { KolResultsTable } from './KolSearch.Table';
import { KolTableContainer } from './KolTableContainer';
import { SmartRoutingTableContainer } from './Message.Response.SmartRoutingFake';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';
import { useDownloadHcpResults } from './hooks/useDownloadHcpResults';

type Props = {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.HcpSelection;
};

function buildInitialState(value: Chat.QueryState.HcpSelection): State {
  return {
    form: {
      kolId: null,
      name: value.name || '',
      location: null,
    },
    results: value.results,
    status: value.status,
  };
}

export const HcpSelectionMessage = (props: Props) => {
  return (
    <KolTableContainer>
      <HcpSelectionMessageContent {...props} />
    </KolTableContainer>
  );
};

const HcpSelectionMessageContent = (props: Props) => {

  const [state, dispatch] = useReducer(reducer, buildInitialState(props.value));
  const chat = useContext(ActiveChatSessionContext);
  const submitAction = useContext(SubmitActionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const inputFocused = useRef(false);

  const submitDisabled = useMemo(() => {
    return (!state.form.name && !state.form.location) || state.status !== KolSearchStatusEnum.FormEntry;
  }, [state.form.location, state.form.name, state.status]);

  const apiClient = useChatApiClient();

  const submitFormMutation = useMutation({
    mutationFn: () => {
      return apiClient.search.searchKols({
        name: state.form.name,
        lat: state.form.location?.latitude,
        lon: state.form.location?.longitude,
        brandIdentifier,
      });
    },
    onMutate: () => {
      dispatch({
        type: 'search-submitted',
      });
    },
    onError: _ => {
      dispatch({
        type: 'search-errored',
      });
    },
    onSuccess: res => {
      dispatch({
        type: 'search-results-available',
        payload: {
          results: res.results,
        },
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      return apiClient.queries.cancelQueryHcpSelection({
        chatInstanceId: chat.id,
        queryIdentifier: props.queryIdentifier,
      });
    },
    onMutate: () => {
      dispatch({ type: 'search-cancelled' });
    },
  });

  const handleHcpSearchInitiated = useCallback(() => {
    return submitFormMutation.mutateAsync();
  }, [submitFormMutation]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(e => {

    if (!inputFocused.current) return;

    if (submitDisabled) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      handleHcpSearchInitiated();
    }
  }, [submitDisabled, handleHcpSearchInitiated]);

  const handleInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const setFormState = useCallback((form: KolSearchForm) => {
    dispatch({ type: 'form-updated', payload: form });
  }, []);

  const handleHcpSelected = useCallback((item: KolSearchResult) => {
    const query = `${props.value.originalQuery}\n\nUse the following HCP: ${item.name}`;
    dispatch({
      type: 'hcp-selected',
    });
    return submitAction({
      type: ChatActionType.ReaskWithHcp,
      payload: {
        hcpId: item.id,
        queryId: props.queryId,
      },
    }, query);
  }, [props.queryId, props.value.originalQuery, submitAction]);

  const handleSearchCancelled = useCallback(() => {
    return cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const handleResetForm = useCallback(() => {
    dispatch({ type: 'form-reset' });
  }, []);

  const { download: downloadHcpResults } = useDownloadHcpResults({
    chatInstanceId: chat.id,
    queryIdentifier: props.queryIdentifier,
    brandIdentifier,
  });

  const isSmartRouting = useIsSmartRouting();

  const isInteractive = useMemo(() => {
    return !['form-submitted', 'form-cancelled', 'form-results', 'hcp-selected'].includes(state.status);
  }, [state.status]);
  const showResults = useMemo(() => {
    return ['form-results'].includes(state.status) && state.results.length > 0;
  }, [state.status, state.results]);
  const showNoResultsMessage = useMemo(() => {
    return ['form-results'].includes(state.status) && state.results.length === 0;
  }, [state.status, state.results]);
  const showLoading = useMemo(() => {
    return ['form-submitted'].includes(state.status);
  }, [state.status]);
  const showCancelButton = useMemo(() => {
    return !['form-cancelled', 'hcp-selected'].includes(state.status);
  }, [state.status]);
  const showStartNewSearch = useMemo(() => {
    return ['form-results'].includes(state.status);
  }, [state.status]);
  const showInputForm = useMemo(() => {
    return ['form-entry', 'form-error'].includes(state.status);
  }, [state.status]);

  const systemCss = useMemo(() => {
    if (state.status !== 'form-results') return undefined;

    return css({
      maxWidth: '100%',
    });
  }, [state.status]);

  const message = `To answer your question, I need help from you to find the right HCP. Please use the search tool below to find the HCP your question is about.`;

  const showDivider = showStartNewSearch;

  useEffect(() => {
    const isSearchWithNoName = ['form-results'].includes(state.status) && state.results.length === 0 && !state.form.name;

    if (isSearchWithNoName) {
      console.log('Resetting form');
      handleResetForm();
    }
  }, [handleResetForm, state.status, state.results.length, state.form.name]);


  return (
    <KolSearchResultsContext.Provider value={state.results}>
      <MessageContainer>
        <BasicSystemUserMessage bodyStyles={systemCss}>
          <Message>{message}</Message>
          {showInputForm && (
            <>
              <KolSearchInputForm
                formState={state.form}
                inputDisabled={!isInteractive}
                onKeyDown={handleKeyDown}
                onInputFocus={handleInputFocus}
                onInputBlur={handleInputBlur}
                onKolSelect={handleHcpSelected}
                setFormState={setFormState} />
              <Divider />
              <SearchQueryHint value='Search for profiles' disabled={submitDisabled} onClick={handleHcpSearchInitiated} />
            </>
          )}
          {showLoading && (
            <div>
              Loading...
            </div>
          )}
          {showResults && (
            <ResultsWrapper>
              {state.form.name ? `Here are the results for ${state.form.name}:` : 'Here are the results:'}<br />
              {props.value.isSmartRouting && (
                <SmartRoutingTableContainer {...props}>
                  <StyledResultsTable
                    isInteractive={true}
                    onSelectProfile={handleHcpSelected}
                  />
                </SmartRoutingTableContainer>
              )}
              {!props.value.isSmartRouting && (
                <KolResultsContainer records={state.results}>
                  <StyledResultsTable
                    isInteractive={true}
                    onSelectProfile={handleHcpSelected}
                  />
                </KolResultsContainer>
              )}

            </ResultsWrapper>
          )}
          {showNoResultsMessage && (
            <NoResults>
              {state.form.name ? `No results found for ${state.form.name}` : 'No results found'}
            </NoResults>
          )}
          {showDivider && <Divider />}
          {showResults && (
            <StyledDownloadButton value="Download List" onClick={downloadHcpResults} />
          )}
          {showResults && isSmartRouting && (
            <GenerateRouteButton
              initialLocation={props.value.location ? {
                type: 'browser',
                latitude: props.value.location.latitude,
                longitude: props.value.location.longitude,
              } : undefined}
            />
          )}
          {showStartNewSearch && (
            <StyledCancelButton
              displayValue="Start a new search"
              onClick={handleResetForm} />
          )}
          {/* {showCancelButton && (
          <StyledCancelButton
            displayValue="Ask another question"
            onClick={handleSearchCancelled} />
        )} */}
        </BasicSystemUserMessage>
      </MessageContainer>
    </KolSearchResultsContext.Provider>
  );
};

const Message = styled.div({
  marginBottom: 15,
});

const StyledDownloadButton = styled(DownloadQueryHint)({
  marginTop: 15,
});

const StyledCancelButton = styled(CancelAction)({
  marginTop: 15,
});

const NoResults = styled.div({
  marginTop: 15,
});

const ResultsWrapper = styled.div({
  marginTop: 15,
});
const StyledResultsTable = styled(KolResultsTable)({
  marginTop: 10,
});

const Divider = styled.div(({ theme }) => ({
  width: '100%',
  height: 2,
  backgroundColor: theme.palette.gray.light2,
  margin: '15px 0',
}));

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'name-changed':
      return {
        ...state,
        form: {
          ...state.form,
          name: action.payload,
        },
      };
    case 'form-updated':
      return {
        ...state,
        form: action.payload,
      };
    case 'results-changed':
      return {
        ...state,
        results: action.payload,
      };
    case 'form-reset':
      return {
        form: {
          kolId: null,
          name: '',
          location: null,
        },
        results: [],
        status: 'form-entry',
      };
    case 'search-errored':
      return {
        ...state,
        status: 'form-error',
      };
    case 'search-submitted':
      return {
        ...state,
        status: 'form-submitted',
      };
    case 'hcp-selected': {
      return {
        ...state,
        status: 'hcp-selected',
      };
    };
    case 'search-cancelled': {
      return {
        ...state,
        status: 'form-cancelled',
      };
    }
    case 'search-results-available': {
      return {
        ...state,
        results: action.payload.results,
        status: 'form-results',
      };
    }
    default:
      return state;
  }
};

type NameChangedAction = {
  type: 'name-changed';
  payload: string;
};
type ResultsChangedAction = {
  type: 'results-changed';
  payload: KolSearchResult[];
};
type FormUpdatedAction = {
  type: 'form-updated';
  payload: KolSearchForm;
};
type FormReset = {
  type: 'form-reset';
};
type SearchErrored = {
  type: 'search-errored';
};
type SearchSubmitted = {
  type: 'search-submitted';
};
type ResultsAvailable = {
  type: 'search-results-available';
  payload: {
    results: KolSearchResult[];
  };
};
type HcpSelected = {
  type: 'hcp-selected';
};
type SearchCancelled = {
  type: 'search-cancelled';
};
type Action =
  NameChangedAction |
  ResultsChangedAction |
  FormUpdatedAction |
  FormReset |
  SearchErrored |
  SearchSubmitted |
  ResultsAvailable |
  HcpSelected |
  SearchCancelled
  ;

type State = {
  form: KolSearchForm;
  results: KolSearchResult[];
  status: Chat.QueryState.HcpSelection['status'];
};